import { getReplay } from '@sentry/angular';
import { registerReplayProbe } from './sentry-replay-transport';

export interface SentryReplayProbeResult {
  replayId: string;
  acceptedSegments: number;
  rejectedSegments: number;
  blockedSegments: number;
  statusCode?: number;
}

/** Explicit Dev-panel action; automatic collection continues using the shared sampling policy. */
export async function runSentryReplayProbe(
  runId: string,
  exercise: () => Promise<void>,
  cleanup: () => void,
): Promise<SentryReplayProbeResult> {
  const replay = getReplay();
  if (!replay) throw new Error('Replay integration unavailable');
  const previous = replay.getRecordingMode();
  await replay.stop();
  let unregister: (() => void) | undefined;
  try {
    replay.start();
    const replayId = replay.getReplayId();
    if (!replayId) throw new Error('Replay could not start');
    const result: SentryReplayProbeResult = {
      replayId,
      acceptedSegments: 0,
      rejectedSegments: 0,
      blockedSegments: 0,
    };
    unregister = registerReplayProbe(replayId, runId, (status, blocked) => {
      if (blocked) {
        result.blockedSegments++;
        return;
      }
      result.statusCode = status;
      if (status !== undefined && status >= 200 && status < 300)
        result.acceptedSegments++;
      else result.rejectedSegments++;
    });
    await exercise();
    await replay.flush();
    await replay.stop();
    return result;
  } finally {
    try {
      await replay.stop({ flush: false });
    } finally {
      unregister?.();
      cleanup();
      if (previous === 'session') replay.start();
      else if (previous === 'buffer') replay.startBuffering();
    }
  }
}
